import React, { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

const PHONE = "918669999901";
const MESSAGE =
  "Hello Vilora Impex, I'd like to discuss an export inquiry. Please share your catalogue and pricing.";
const WA_LINK = `https://wa.me/${PHONE}?text=${encodeURIComponent(MESSAGE)}`;

const WhatsAppIcon = ({ size = 22 }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    fill="currentColor"
    aria-hidden="true"
  >
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.298-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
  </svg>
);

export default function FloatingWhatsApp() {
  const [showHint, setShowHint] = useState(false);
  const [hintDismissed, setHintDismissed] = useState(false);

  useEffect(() => {
    if (sessionStorage.getItem("vilora_wa_hint_dismissed")) {
      setHintDismissed(true);
      return;
    }
    // Show hint only on desktop (>= 1024px) to avoid overlapping mobile content
    if (window.innerWidth < 1024) return;
    const t = setTimeout(() => setShowHint(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const dismissHint = (e) => {
    e?.stopPropagation();
    setShowHint(false);
    setHintDismissed(true);
    sessionStorage.setItem("vilora_wa_hint_dismissed", "1");
  };

  return (
    <div
      className="vi-fab-whatsapp fixed right-4 sm:right-6 z-50 flex flex-col items-end gap-3"
      style={{ bottom: "max(1rem, env(safe-area-inset-bottom))" }}
      data-testid="floating-whatsapp-wrapper"
    >
      <AnimatePresence>
        {showHint && !hintDismissed && (
          <motion.div
            initial={{ opacity: 0, x: 10, scale: 0.95 }}
            animate={{ opacity: 1, x: 0, scale: 1 }}
            exit={{ opacity: 0, x: 10, scale: 0.95 }}
            transition={{ duration: 0.45, ease: "easeOut" }}
            className="absolute bg-[#FFFFFF] border border-[#E8943A]/40 px-4 py-3 max-w-[240px] shadow-[0_6px_28px_rgba(0,0,0,0.4)]"
            style={{
              right: "calc(100% + 14px)",
              bottom: 0,
              transformOrigin: "right center",
            }}
            data-testid="whatsapp-hint"
          >
            <div className="velora-meta text-[#7B3F00] mb-1.5">Export Desk</div>
            <p className="velora-sans text-xs text-[#3D1F00]/85 leading-snug">
              Reach our team on WhatsApp - typically replies within an hour.
            </p>
            <button
              type="button"
              aria-label="Dismiss"
              onClick={dismissHint}
              className="absolute -top-2 -right-2 w-5 h-5 bg-[#FAF3E0] border border-[#E8943A]/40 text-[#7B3F00] text-xs leading-none flex items-center justify-center hover:bg-[#E8943A] hover:text-[#FFFFFF] transition-colors"
            >
              ×
            </button>
            {/* Pointer - caret on the RIGHT edge, pointing toward the WA icon */}
            <div
              className="absolute w-3 h-3 bg-[#FFFFFF] border-t border-r border-[#E8943A]/40 rotate-45"
              style={{ right: "-6px", bottom: "20px" }}
            />
          </motion.div>
        )}
      </AnimatePresence>

      {/* The button */}
      <a
        href={WA_LINK}
        target="_blank"
        rel="noopener noreferrer"
        data-testid="floating-whatsapp"
        aria-label="Chat with Vilora Impex on WhatsApp"
        onClick={() => setShowHint(false)}
        className="relative group"
        style={{ marginBottom: "var(--wa-mobile-offset, 0px)" }}
      >
        {/* Pulsing ring */}
        <span
          className="absolute inset-0 rounded-full pointer-events-none"
          style={{
            background: "rgba(37, 211, 102, 0.45)",
            animation: "waPulse 2.2s ease-out infinite",
          }}
        />
        <span
          className="relative flex items-center justify-center w-14 h-14 sm:w-15 sm:h-15 rounded-full text-white shadow-[0_8px_28px_rgba(37,211,102,0.45)] transition-transform duration-500 group-hover:scale-105"
          style={{
            background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
            border: "2px solid rgba(255,255,255,0.18)",
          }}
        >
          <WhatsAppIcon size={26} />
        </span>
      </a>

      <style>{`
        @keyframes waPulse {
          0% { transform: scale(1); opacity: 0.55; }
          70% { transform: scale(1.6); opacity: 0; }
          100% { transform: scale(1.6); opacity: 0; }
        }
        @media (max-width: 1023px) {
          [data-testid="floating-whatsapp-wrapper"] { --wa-mobile-offset: 0px; }
        }
      `}</style>
    </div>
  );
}
